import moment from 'moment';
import { GanttEntryTypesEnum, GanttEntryWorkingTypesEnum } from '@thinknet-gmbh/platform-common';
import { PLATFORM_THEME_COLORS } from '@thinknet-gmbh/platform-common-ui';

import {
  ABSENCE_COLORS_TO_TONES_RELATIONS,
  DATE_FORMAT_KEY,
  COLUMN_HEIGHT,
} from 'src/pages/gantt/gantt-calendar/gantt-view/gantt-view.constants';
import { GANTT_VIEW_POPOVER_TYPES } from 'src/pages/gantt/gantt-calendar/gantt-view/components/gantt-popover/gantt-popover.constants';

const { lightGreen, lightYellow, green, yellow } = PLATFORM_THEME_COLORS.tone;

export const getEntryColors = ({ type, source }: { type: any; source: any }) => {
  const isTimeStripe = type === GanttEntryTypesEnum.TIME_STRIPE;
  const { confirmed, color } = source;

  if (isTimeStripe) {
    return confirmed ? [lightGreen, green] : [lightYellow, yellow];
  }

  return [ABSENCE_COLORS_TO_TONES_RELATIONS[color], color];
};

const onEntryMouseEnter =
  (isValidColumnSize: boolean) =>
  ({ currentTarget }: any) => {
    const { children, parent } = currentTarget;
    const [rect, text] = children;

    if (!isValidColumnSize) {
      rect.to({
        duration: 0.1,
        height: COLUMN_HEIGHT - 2,
        onFinish: () => text.setAttr('width', COLUMN_HEIGHT - 2),
      });
    }

    currentTarget.setAttr('zIndex', parent.children.length - 1);
    rect.setAttrs({ shadowOpacity: 0.1 });

    document.body.style.cursor = 'pointer';
  };

const onEntryMouseLeave = (isValidColumnSize: boolean, height: number) => (event: any) => {
  const [rect, text] = event.currentTarget.children;

  if (!isValidColumnSize) {
    rect.to({
      height: height - 2,
      duration: 0.1,
      onFinish: () => text.setAttr('width', 0),
    });
  }
  event.currentTarget.setAttr('zIndex', 0);
  rect.setAttrs({ shadowOpacity: 0 });

  document.body.style.cursor = 'default';
};

export const onEntryHoverHandler = (height: number) => {
  const isValidColumnSize = COLUMN_HEIGHT <= height;

  return {
    onMouseEnter: onEntryMouseEnter(isValidColumnSize),
    onMouseLeave: onEntryMouseLeave(isValidColumnSize, height),
  };
};

export const onEntryClickHandler =
  ({ entryView, entry, setPopoverInfo, click }: any) =>
  () => {
    const { absenceInfo, timeStripeInfo } = GANTT_VIEW_POPOVER_TYPES;
    const isTimeStripe = entry.type === GanttEntryTypesEnum.TIME_STRIPE;
    const popoverType = isTimeStripe ? timeStripeInfo : absenceInfo;

    setPopoverInfo({ type: popoverType, target: { ...entry, ...entry.source }, entryView });
  };

export const getAdditionalHeight = (
  ganttRecordsByDateMap: any,
  { endDateString }: any,
  dateMapByDate: any,
  dateString: any
) => {
  let additionalHeight = 0;

  const date = moment(dateString, DATE_FORMAT_KEY);
  const endDate = moment(endDateString, DATE_FORMAT_KEY);

  for (
    const dateItem = moment(date).add(1, 'day');
    dateItem.isSameOrBefore(endDate);
    dateItem.add(1, 'day')
  ) {
    const dateInFormat = dateItem.format(DATE_FORMAT_KEY);

    if (dateMapByDate[dateInFormat]) {
      const { entries = [] } = ganttRecordsByDateMap[dateInFormat] || {};
      const columnHeight = COLUMN_HEIGHT / entries.length;

      additionalHeight += columnHeight;
    }
  }

  return Number.isFinite(additionalHeight) ? additionalHeight : COLUMN_HEIGHT;
};

export const checkIfStartEntryInView = (
  ganttRecordsByDateMap: any,
  { groupId, startDateString }: any,
  dateMapByDate: any,
  dateString: string
) => {
  // by default hide entry if his working type is not start working
  const isTargetEntryInGanttView = dateMapByDate[dateString];
  let showEntryInGanttView = false;

  // show entry if this entry exists in view and his type is start working
  if (isTargetEntryInGanttView) {
    showEntryInGanttView = true;
  }

  const date = moment(dateString, DATE_FORMAT_KEY);
  const startDate = moment(startDateString, DATE_FORMAT_KEY);

  for (
    const dateItem = moment(date).subtract(1, 'day');
    dateItem.isSameOrAfter(startDate);
    dateItem.subtract(1, 'day')
  ) {
    const dateInFormat = dateItem.format(DATE_FORMAT_KEY);
    const { entries = [] } = ganttRecordsByDateMap[dateInFormat] || {};
    const target = entries.find((doc: any) => doc.groupId === groupId);
    const isPrevTargetEntryInGanttView = dateMapByDate[dateInFormat];

    if (isPrevTargetEntryInGanttView && target) {
      showEntryInGanttView = false;
    }
  }

  return showEntryInGanttView;
};

export const getCornerRadiusForEntry = (
  ganttRecordsByDateMap: any,
  { groupId, startDateString, endDateString }: any,
  dateMapByDate: any
) => {
  const cornerBorders = [4, 4, 4, 4];

  if (startDateString === endDateString) return cornerBorders;

  const startDate = moment(startDateString, DATE_FORMAT_KEY);
  const endDate = moment(endDateString, DATE_FORMAT_KEY);

  const entriesTypes = [];

  for (
    const dateItem = moment(startDate);
    dateItem.isSameOrBefore(endDate);
    dateItem.add(1, 'day')
  ) {
    const dateInFormat = dateItem.format(DATE_FORMAT_KEY);

    if (dateMapByDate[dateInFormat]) {
      const { entries = [] } = ganttRecordsByDateMap[dateInFormat] || {};
      const { workingType } = entries.find((doc: any) => doc.groupId === groupId) || {};

      if (workingType) {
        entriesTypes.push(workingType);
      }
    }
  }

  if (!entriesTypes.includes(GanttEntryWorkingTypesEnum.START_WORKING)) {
    cornerBorders[0] = 0;
    cornerBorders[1] = 0;
  }

  if (!entriesTypes.includes(GanttEntryWorkingTypesEnum.END_WORKING)) {
    cornerBorders[2] = 0;
    cornerBorders[3] = 0;
  }

  return cornerBorders;
};
